import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { getLoginPath } from '@/lib/loginPath';

const highlights = [
  {
    icon: 'fa-solid fa-building-user',
    title: '多站点',
    text: '不同社区、商圈独立配置，数据互不干扰，切换站点即可管理对应登记信息。',
  },
  {
    icon: 'fa-solid fa-clipboard-list',
    title: '登记接入',
    text: '与常用在线表单打通，市民在手机端填写转让信息，后台自动汇总，省去人工抄录。',
  },
  {
    icon: 'fa-solid fa-images',
    title: '图文素材',
    text: '支持多图与文字说明，自动整理展示所需素材，为后续发布做好准备。',
  },
  {
    icon: 'fa-solid fa-calendar-week',
    title: '展示有效期',
    text: '按规则计算信息展示起止时间，过期信息便于统一运营与下架策略。',
  },
  {
    icon: 'fa-brands fa-weixin',
    title: '公众号协作',
    text: '在获得授权的前提下，可将内容同步至微信公众号草稿，便于小编审核后再发布。',
  },
  {
    icon: 'fa-solid fa-chart-line',
    title: '日志与追溯',
    text: '关键操作与同步结果可追溯，方便排查问题与对外说明。',
  },
] as const;

const journey = [
  {
    n: '01',
    title: '市民登记',
    desc: '通过表单提交物品描述、联系方式与图片，操作简单、随时随地。',
  },
  {
    n: '02',
    title: '后台汇聚',
    desc: '信息进入贴图虾统一查看、筛选与维护，运营人员一站式处理。',
  },
  {
    n: '03',
    title: '内容发布',
    desc: '按站点规则生成适合微信阅读的内容形态，进入草稿后再由团队确认发布。',
  },
] as const;

const linkBtnPrimary =
  'inline-flex items-center justify-center rounded-xl bg-gradient-to-b from-teal-500 to-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-teal-900/20 ring-1 ring-teal-500/25 transition hover:from-teal-400 hover:to-teal-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600';
const linkBtnSecondary =
  'inline-flex items-center justify-center rounded-xl border border-slate-200/90 bg-white/90 px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm backdrop-blur-sm transition hover:border-slate-300 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500/40';

export function HomePage() {
  return (
    <div className="space-y-10 pb-4">
      <section className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white/95 shadow-card backdrop-blur-sm">
        <div
          className="absolute inset-0 bg-[linear-gradient(135deg,rgba(45,212,191,0.12)_0%,transparent_45%,rgba(148,163,184,0.08)_100%)]"
          aria-hidden
        />
        <div className="relative grid gap-10 px-6 py-12 sm:px-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:py-16 lg:pl-12 lg:pr-10">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-teal-200/80 bg-teal-50/90 px-3 py-1 text-xs font-medium text-teal-900">
              <i className="fa-solid fa-shrimp text-teal-600" aria-hidden />
              贴图虾
            </span>
            <h1 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl sm:leading-[1.15]">
              让社区信息与贴图
              <br className="hidden sm:block" />
              好登记、好管理、好发布
            </h1>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-600 sm:text-base">
              面向物业、业委会与本地生活运营团队，把散落在表单里的转让信息收拢到一处；统一风格、统一节奏，再与微信公众号协作完成对外展示。
            </p>
            <p className="mt-3 max-w-xl text-xs leading-relaxed text-slate-500 sm:text-sm">
              系统管理员与普通会员使用同一登录入口，登录后按账号权限使用功能（如站点管理、登记与公众号协作等）。
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={getLoginPath()} className={linkBtnPrimary}>
                登录后台
              </Link>
              <a href="#features" className={linkBtnSecondary}>
                了解核心能力
              </a>
            </div>
          </div>
          <div className="relative mx-auto w-full max-w-md lg:mx-0 lg:max-w-none">
            <div className="rounded-2xl border border-slate-100 bg-gradient-to-b from-slate-50 to-white p-6 shadow-inner">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">产品要点</p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                登录后可选择站点，集中查看登记信息、配置展示样式；在授权前提下与微信公众号协作完成对外发布。具体菜单因账号角色略有不同。
              </p>
              <div className="mt-6 flex gap-3 border-t border-slate-100 pt-6">
                <div className="flex-1 rounded-xl bg-teal-50/80 px-3 py-3 text-center">
                  <p className="text-[11px] font-medium text-teal-800">一站式</p>
                  <p className="mt-0.5 text-xs text-teal-900/80">登记与运营</p>
                </div>
                <div className="flex-1 rounded-xl bg-slate-100/80 px-3 py-3 text-center">
                  <p className="text-[11px] font-medium text-slate-700">可协作</p>
                  <p className="mt-0.5 text-xs text-slate-600">公众号团队</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features">
        <div className="mb-6 text-center sm:mb-8">
          <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">核心能力</h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600 sm:text-[15px]">
            围绕「收进来、理清楚、发出去」设计，界面简洁，降低日常维护成本。
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {highlights.map((h) => (
            <Card key={h.title} className="transition hover:border-teal-200/80 hover:shadow-md">
              <div className="flex gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-icon text-lg text-white shadow-md shadow-teal-900/20 ring-1 ring-white/10">
                  <i className={h.icon} aria-hidden />
                </span>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{h.text}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200/70 bg-gradient-to-b from-slate-50/90 to-white/60 px-6 py-10 shadow-sm shadow-slate-900/[0.04] backdrop-blur-sm sm:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">使用路径</h2>
          <p className="mt-2 text-sm text-slate-600 sm:text-[15px]">三步理解贴图虾在业务中的位置</p>
        </div>
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 md:grid-cols-3">
          {journey.map((j, i) => (
            <div key={j.n} className="relative text-center">
              {i < journey.length - 1 ? (
                <div
                  className="absolute left-[60%] top-8 hidden h-px w-[80%] bg-gradient-to-r from-teal-200 to-transparent md:block"
                  aria-hidden
                />
              ) : null}
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-lg font-bold text-teal-700 shadow-sm ring-1 ring-slate-200/80">
                {j.n}
              </span>
              <h3 className="mt-4 text-base font-semibold text-slate-900">{j.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{j.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-teal-200/60 bg-gradient-to-r from-teal-600 to-teal-800 px-6 py-10 text-center shadow-lg sm:px-10">
        <h2 className="text-lg font-semibold text-white sm:text-xl">已有账号？</h2>
        <p className="mx-auto mt-2 max-w-lg text-sm text-teal-100">
          使用分配的用户名与密码登录，进入首页、信息登记与站点管理等模块，与团队一起维护社区信息与贴图内容。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to={getLoginPath()}
            className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-teal-800 shadow-md shadow-teal-950/20 transition hover:bg-teal-50"
          >
            登录后台
          </Link>
          <a
            href="#features"
            className="inline-flex items-center justify-center rounded-xl border border-white/40 bg-transparent px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            再看一眼能力介绍
          </a>
        </div>
      </section>
    </div>
  );
}
